import {
  INTEGRATION_PROVIDERS,
  SYNC_STATUSES,
  type CustomerIntegrationEntity,
  type StripeCustomerEntity,
  type StripeIntermediateMetadata,
  type StripeMetadata,
} from '@vy/types';
import { isEmptyArray, isNotEmptyString, isNotNullOrUndefined, isNullOrUndefined, vyDate } from '@vy/utils';
import type Stripe from 'stripe';

export function mergeUniqueStripeCustomers(
  existingCustomers: Stripe.Customer[],
  newCustomers: Stripe.Customer[],
) {
  const existingIds = new Set(existingCustomers.map((customer) => customer.id));

  return [...existingCustomers, ...newCustomers.filter((customer) => !existingIds.has(customer.id))];
}

export function stripeCustomersToIntermediateData(
  customers: Stripe.Customer[] | undefined,
): StripeIntermediateMetadata[] {
  if (isNullOrUndefined(customers) || isEmptyArray(customers)) {
    return [];
  }

  return customers.map<StripeIntermediateMetadata>(({ id, name, email }) => ({
    stripeId: id,
    stripeName: formatStripeCustomerName(name, email, id),
    stripeEmail: email,
    integrationSource: INTEGRATION_PROVIDERS.stripe,
  }));
}

export function formatStripeCustomerName(
  name: string | null | undefined,
  email: string | null | undefined,
  _id: string,
): string {
  return isNotEmptyString(name) ? name : 'Unnamed Customer';
}

export function removeStripeEntity(
  entities: CustomerIntegrationEntity[] | undefined | null,
): CustomerIntegrationEntity[] {
  return isNotNullOrUndefined(entities)
    ? entities.filter((entity) => entity?.integrationProvider !== INTEGRATION_PROVIDERS.stripe)
    : [];
}

export function createStripeEntity(stripeMetadata: StripeIntermediateMetadata): CustomerIntegrationEntity {
  return {
    integrationProvider: INTEGRATION_PROVIDERS.stripe,
    integrationExternalId: stripeMetadata.stripeId,
    updatedAt: vyDate().toDate(),
    syncStatus: SYNC_STATUSES.None,
    metadata: {
      name: stripeMetadata.stripeName,
      email: stripeMetadata.stripeEmail,
    },
  };
}

export function toStripeIntermediateMetadata(
  entity: StripeCustomerEntity | undefined | null,
): StripeIntermediateMetadata | null {
  if (isNullOrUndefined(entity)) {
    return null;
  }

  const metadata: StripeMetadata = entity.metadata;
  const displayName = metadata?.name || `Stripe - ${entity.integrationExternalId}`;

  const result = {
    stripeId: entity.integrationExternalId,
    stripeName: displayName,
    stripeEmail: metadata?.email,
    integrationSource: INTEGRATION_PROVIDERS.stripe,
  };

  return result;
}
